import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerToastHandler } from '@wlb/shared/platform/notifier';
import type { ToastShowOptions } from '@wlb/shared/platform/notifier';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from './ui/Avatar';
import { CloseIcon } from './ui/icons';
import './toast.css';

interface ToastEntry extends ToastShowOptions {
  id: number;
}

let counter = 0;

export function Toast() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    registerToastHandler((options) => {
      const id = ++counter;
      setToasts((prev) => [...prev, { ...options, id }]);
      const duration = options.duration ?? 3000;
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((item) => item.id !== id));
        }, duration);
      }
    });
    return () => registerToastHandler(null);
  }, []);

  return (
    <div className="toast-container" role="region" aria-label={t('common.toasts.region')}>
      {toasts.map((toast) => {
        const isRich = Boolean(toast.title);

        if (isRich) {
          return (
            <div
              key={toast.id}
              className="toast toast--message"
              role="alert"
              aria-label={t('chatNotifications.newMessageAriaLabel', {
                name: toast.title,
              })}
            >
              {toast.actionPath && (
                <button
                  type="button"
                  className="toast__action"
                  aria-label={t('chatNotifications.openConversation')}
                  onClick={() => {
                    navigate(toast.actionPath!);
                    dismiss(toast.id);
                  }}
                >
                  <Avatar
                    src={toast.avatar ?? undefined}
                    name={toast.title}
                    seed={toast.avatarSeed ?? toast.title}
                    size="sm"
                    className="toast__avatar"
                  />
                  <span className="toast__content">
                    {toast.title && (
                      <span className="toast__title">{toast.title}</span>
                    )}
                    {toast.subtitle && (
                      <span className="toast__subtitle">{toast.subtitle}</span>
                    )}
                  </span>
                </button>
              )}
              {!toast.actionPath && (
                <>
                  <Avatar
                    src={toast.avatar ?? undefined}
                    name={toast.title}
                    seed={toast.avatarSeed ?? toast.title}
                    size="sm"
                    className="toast__avatar"
                  />
                  <span className="toast__content">
                    {toast.title && (
                      <span className="toast__title">{toast.title}</span>
                    )}
                    {toast.subtitle && (
                      <span className="toast__subtitle">{toast.subtitle}</span>
                    )}
                  </span>
                </>
              )}
              <button
                type="button"
                className="toast__close"
                aria-label={t('common.buttons.close')}
                onClick={() => dismiss(toast.id)}
              >
                <CloseIcon size={16} />
              </button>
            </div>
          );
        }

        return (
          <div
            key={toast.id}
            className={`toast toast--simple toast-${toast.type ?? 'info'}`}
            role="alert"
          >
            {toast.message}
          </div>
        );
      })}
    </div>
  );
}
